import { formatDateToISO } from "utils";

import { orderTypesPayloadMap } from "../constants";
import { DEFAULT_SELLER_SORT } from "./constants";

const dashboardDateTypePayloadMap = {
  "FG readiness": "FG_READY",
  "Order confirmation": "ORD_CNF",
};

const getNumber = (...values) => {
  const value = values.find((item) => item !== undefined && item !== null);
  const parsed = Number(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const getSellerVmId = (seller = {}) => seller.vmId ?? seller.VmId;

const getSellerVmIds = (sellers = []) =>
  sellers.map((seller) => getSellerVmId(seller)).filter(Boolean);

const getVmIdsBySellerNames = (sellerList = [], sellerNames = []) => {
  if (!sellerList.length || !sellerNames.length) return [];

  const vmIdMap = sellerList.reduce((map, seller) => {
    const companyName = seller.companyName;
    const vmId = getSellerVmId(seller);

    if (!vmId) return map;

    if (!map.has(companyName)) {
      map.set(companyName, []);
    }

    map.get(companyName).push(vmId);

    return map;
  }, new Map());

  return sellerNames.flatMap((name) => vmIdMap.get(name) || []);
};

const getVmIdsByStates = (sellerList = [], states = []) => {
  if (!sellerList.length || !states.length) return [];

  return sellerList
    .filter(({ state }) => states.includes(state))
    .map((seller) => getSellerVmId(seller))
    .filter(Boolean);
};

const buildSellerAnalyticsPayload = (
  activeFilters = {},
  activeSort = DEFAULT_SELLER_SORT,
  sellerPage = 0,
  sellerRowsPerPage = 10,
) => {
  const { customerNames: _omit, ...sellerFilters } = activeFilters;
  return {
    page: sellerPage + 1,
    pageSize: sellerRowsPerPage,
    ...sellerFilters,
    sortBy: activeSort.sortBy,
    sortDir: activeSort.sortDir,
  };
};

const buildSellerFilters = (filterPayload = {}, sellerList = []) => {
  const {
    dateFrom,
    dateTo,
    dateType,
    sellerNames,
    sellerStates,
    resolvedCustomers = [],
    orderType,
    deliveryType,
    buyerOrderNo,
  } = filterPayload;
  const sellerVmIds = getVmIdsBySellerNames(sellerList, sellerNames);
  const stateVmIds = getVmIdsByStates(sellerList, sellerStates);

  return {
    customers: resolvedCustomers.map((c) => c.gstin).filter(Boolean),
    customerNames: resolvedCustomers.map((c) => c.companyName).filter(Boolean),
    dateFrom: formatDateToISO(dateFrom),
    dateTo: formatDateToISO(dateTo),
    dateType: dashboardDateTypePayloadMap[dateType],
    orderType: orderTypesPayloadMap[orderType],
    deliveryType: deliveryType === "ALL" ? "" : deliveryType,
    vmIds: [...new Set([...sellerVmIds, ...stateVmIds])],
    buyerOrderNo,
  };
};

const getNextSellerSort = (currentSort, sortBy) => ({
  sortBy,
  sortDir:
    currentSort.sortBy === sortBy && currentSort.sortDir === "ASC"
      ? "DESC"
      : "ASC",
});

const buildOrdersSearchPayload = ({
  page,
  rowsPerPage,
  filters,
  selectedVmIds,
  tableFilters,
  sortConfig,
}) => {
  const { customers: _gstins, customerNames, ...orderFilters } = filters;
  return {
    page: page + 1,
    pageSize: rowsPerPage,
    ...orderFilters,
    ...(customerNames?.length ? { customers: customerNames } : {}),
    ...(selectedVmIds.length ? { vmIds: selectedVmIds } : {}),
    fgTab: tableFilters,
    sortBy: sortConfig.sortBy,
    sortDir: sortConfig.sortDir,
  };
};

const normalizeSellerAnalyticsData = ({ data = {} } = {}) => {
  return {
    pagination: data.pagination || {},
    sellers: (Array.isArray(data.sellers) ? data.sellers : []).map(
      (seller) => ({
        ...seller,
        vmId: getSellerVmId(seller),
        companyName: seller.companyName || seller.sellerName || "-",
        state: seller.state || seller.sellerState || "",
        maxAgeing: getNumber(seller.maxAgeing, seller.maxAge, seller.maxAging),
        fgReadiness: getNumber(seller.fgReadiness, seller.fgAdherence),
        fgReadinessStatus:
          seller.fgReadinessStatus ||
          seller.fgStatus ||
          seller.fgDueStatus ||
          seller.fgReadinessTone ||
          "",
        overdueOrders: getNumber(seller.overdueOrders, seller.overDueOrders),
        openOrders: getNumber(seller.openOrders, seller.ordersPending),
        tonnageDue: getNumber(seller.tonnageDue, seller.pendingTonnage),
        onTime: getNumber(seller.onTime, seller.onTimeOrders),
        inFull: getNumber(seller.inFull, seller.inFullOrders),
        status: seller.status || seller.productionStatus || "",
      }),
    ),
  };
};

const getMetricTone = (value, thresholds = {}) => {
  const number = getSafeNumber(value);

  const ranges = [
    ["green", thresholds.greenMin, thresholds.greenMax],
    ["amber", thresholds.amberMin, thresholds.amberMax],
    ["red", thresholds.redMin, thresholds.redMax],
  ];

  return (
    ranges.find(([, min, max]) => number >= min && number <= max)?.[0] || "red"
  );
};

const getSafeNumber = (value) => {
  const parsed = Number(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const formatPercent = (value) => `${Math.round(getSafeNumber(value))}%`;
const formatTonnage = (value) => `${getSafeNumber(value).toFixed(1)} MT`;

const getStatusTone = (status, thresholds = {}) => {
  if (!status) return "red";

  const normalizedStatus = String(status).trim().toUpperCase();

  if ((thresholds.greenStatuses || []).includes(normalizedStatus)) {
    return "green";
  }

  if ((thresholds.amberStatuses || []).includes(normalizedStatus)) {
    return "amber";
  }

  return "red";
};

const getToneColor = (theme, tone) => {
  if (tone === "green") return theme.palette.green.dark;
  if (tone === "amber") return theme.palette.warning.dark;
  return theme.palette.error.light;
};

const getAgeingBgColor = (theme, tone) => {
  if (tone === "green") return theme.palette.green.light;
  if (tone === "amber") return theme.palette.warning.light;
  return theme.palette.error.errorLightBgColor;
};

export {
  buildOrdersSearchPayload,
  buildSellerAnalyticsPayload,
  buildSellerFilters,
  formatPercent,
  formatTonnage,
  getAgeingBgColor,
  getMetricTone,
  getSafeNumber,
  getSellerVmId,
  getSellerVmIds,
  getNextSellerSort,
  getStatusTone,
  getToneColor,
  normalizeSellerAnalyticsData,
};